import React from 'react';
import { socialPlatforms } from '../data/socialPlatforms';

/**
 * SocialIcons - Renders social media icons with customizable styling
 *
 * @param {Object} socialLinks - Object with platform IDs as keys and usernames as values
 * @param {Object} socialStyle - Style settings: { style, shape, color, location }
 * @param {Object} socialVerified - Object with platform IDs as keys and true/false (OAuth-verified)
 * @param {string} size - Icon size: 'sm', 'md', or 'lg'
 * @param {string} primaryColor - Primary color from site theme (for 'primary' color option)
 */
const SocialIcons = ({
  socialLinks = {},
  socialStyle = {},
  socialVerified = {},
  size = 'md',
  primaryColor = '#5D1CE1'
}) => {
  const iconStyle = socialStyle.style || 'filled';
  const iconShape = socialStyle.shape || 'circle';
  const iconColor = socialStyle.color || 'brand';

  const sizeMap = {
    sm: { container: 'w-9 h-9', icon: 'w-5 h-5', iconPx: 20, badge: 12 },
    md: { container: 'w-11 h-11', icon: 'w-6 h-6', iconPx: 24, badge: 14 },
    lg: { container: 'w-14 h-14', icon: 'w-8 h-8', iconPx: 32, badge: 18 }
  };

  const sizes = sizeMap[size];

  // Filter to platforms with values
  const activePlatforms = socialPlatforms.filter(
    platform => socialLinks[platform.id]?.trim()
  );

  if (activePlatforms.length === 0) return null;

  const getBorderRadius = () => {
    switch (iconShape) {
      case 'rounded': return '8px';
      case 'square': return '0px';
      default: return '50%';
    }
  };

  const getColor = (platform) => {
    switch (iconColor) {
      case 'primary': return primaryColor;
      case 'monochrome': return '#6B7280';
      default: return platform.color;
    }
  };

  const borderRadius = getBorderRadius();

  // Verified checkmark badge overlay
  const VerifiedBadge = ({ badgeSize }) => (
    <span
      style={{
        position: 'absolute',
        bottom: -2,
        right: -2,
        width: badgeSize,
        height: badgeSize,
        background: '#10B981',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1.5px solid white',
        zIndex: 1,
      }}
      title="Verified"
    >
      <svg
        viewBox="0 0 12 12"
        fill="none"
        style={{ width: badgeSize * 0.6, height: badgeSize * 0.6 }}
      >
        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );

  const renderIcon = (platform) => {
    const url = platform.urlPattern + socialLinks[platform.id];
    const color = getColor(platform);
    const isVerified = socialVerified[platform.id] === true;

    const wrapperStyle = {
      position: 'relative',
      display: 'inline-flex',
    };

    if (iconStyle === 'filled') {
      return (
        <span key={platform.id} style={wrapperStyle}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${sizes.container} flex items-center justify-center transition-opacity hover:opacity-80`}
            style={{ backgroundColor: color, borderRadius }}
            title={platform.name}
          >
            <span style={{ display: 'inline-flex', width: sizes.iconPx, height: sizes.iconPx, color: 'white' }}>{platform.icon}</span>
          </a>
          {isVerified && <VerifiedBadge badgeSize={sizes.badge} />}
        </span>
      );
    }

    if (iconStyle === 'outlined') {
      return (
        <span key={platform.id} style={wrapperStyle}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${sizes.container} flex items-center justify-center transition-all hover:scale-110`}
            style={{ borderColor: color, color: color, borderWidth: '2px', borderStyle: 'solid', borderRadius }}
            title={platform.name}
          >
            <span style={{ display: 'inline-flex', width: sizes.iconPx, height: sizes.iconPx }}>{platform.icon}</span>
          </a>
          {isVerified && <VerifiedBadge badgeSize={sizes.badge} />}
        </span>
      );
    }

    // Minimal
    return (
      <span key={platform.id} style={wrapperStyle}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${sizes.container} flex items-center justify-center transition-all hover:scale-110`}
          style={{ color: color }}
          title={platform.name}
        >
          <span style={{ display: 'inline-flex', width: sizes.iconPx, height: sizes.iconPx }}>{platform.icon}</span>
        </a>
        {isVerified && <VerifiedBadge badgeSize={sizes.badge} />}
      </span>
    );
  };

  return (
    <div className="flex items-center gap-2">
      {activePlatforms.map(renderIcon)}
    </div>
  );
};

export default SocialIcons;
